import type {
  DateTime,
  DebtorDeliveryMethod,
  DebtorLanguage,
  DebtorPaymentMethod,
  EInvoiceBank,
  EInvoiceCompany,
  EmailAddress,
  EmailDelivery,
  MailDelivery,
  SmsDelivery,
  SwedishId,
} from "$apis/parkito2/index.ts";
import { isPersonVariant } from "$shared/utils/validation.ts";
import type { Debtor } from "$apis/parkito2/types.gen.ts";

export default class DebtorModel implements Debtor {
  constructor(public data: Debtor) {}

  get address(): string | undefined {
    return this.data.address ?? undefined;
  }
  get addressCo(): string | undefined {
    return this.data.addressCo ?? undefined;
  }
  get apartmentNr(): string | undefined {
    return this.data.apartmentNr ?? undefined;
  }
  get city(): string | undefined {
    return this.data.city ?? undefined;
  }
  get contactEmail(): EmailAddress | undefined {
    return this.data.contactEmail ?? undefined;
  }
  get createdAt(): DateTime {
    return this.data.createdAt;
  }
  get deliveryMethod(): DebtorDeliveryMethod | undefined {
    return this.data.deliveryMethod ?? undefined;
  }
  get id(): string {
    return this.data.id;
  }
  get language(): DebtorLanguage | undefined {
    return this.data.language ?? undefined;
  }
  get name(): string {
    return this.data.name;
  }
  get paymentMethod(): DebtorPaymentMethod {
    return this.data.paymentMethod;
  }
  get phone(): string | undefined {
    return this.data.phone ?? undefined;
  }
  get protectedIdentity(): boolean {
    return this.data.protectedIdentity;
  }
  get protectedMailDelivery(): boolean {
    return this.data.protectedMailDelivery;
  }
  get sid(): SwedishId {
    return this.data.sid;
  }
  get updatedAt(): DateTime | undefined {
    return this.data.updatedAt ?? undefined;
  }
  get yourReference(): string | undefined {
    return this.data.yourReference ?? undefined;
  }
  get zip(): string | undefined {
    return this.data.zip ?? undefined;
  }

  isPerson() {
    return isPersonVariant(this.data.sid);
  }
  getSid(): string {
    if (isPersonVariant(this.data.sid)) {
      return this.data.sid.person;
    } else {
      return this.data.sid.organisation;
    }
  }

  translatePaymentMethodType() {
    const type = this.getPaymentMethodType();

    switch (type) {
      case "invoice":
        return "Faktura";
      case "autogiro":
        return "Autogiro";
      case undefined:
        return undefined;
    }
  }

  getPaymentMethodType(): "invoice" | "autogiro" | undefined {
    const method = this.data.paymentMethod;
    if (method === "invoice") {
      return method;
    } else if (typeof method === "object" && "autogiro" in method) {
      return "autogiro";
    }
  }

  getDeliveryMethodType(): string | undefined {
    const method = this.data.deliveryMethod;
    if (typeof method === "string") {
      return method; // 'none' or 'kivra'
    } else if (method) {
      if (deliveryMethodIsMail(method)) {
        // Perform actions specific to MailDelivery
        return "mail";
      } else if (deliveryMethodIsEmail(method)) {
        // Perform actions specific to EmailDelivery
        return "email";
      } else if (deliveryMethodIsSms(method)) {
        // Perform actions specific to SmsDelivery
        return "sms";
      } else if (deliveryMethodIsEInvoicePerson(method)) {
        // Perform actions specific to EInvoiceBank
        return "eInvoicePerson";
      } else if (deliveryMethodIsEInvoiceCompany(method)) {
        // Perform actions specific to EInvoiceCompany
        return "eInvoiceCompany";
      }
    }
    return undefined;
  }
  translateDeliveryMethodType(): string | undefined {
    const type = this.getDeliveryMethodType();
    switch (type) {
      case "kivra":
        return "Kivra";
      case "mail":
        return "Post";
      case "email":
        return "E-post";
      case "sms":
        return "SMS";
      case "eInvoicePerson":
        return "E-faktura privat";
      case "eInvoiceCompany":
        return "E-faktura företag";
      case undefined:
        return undefined;
    }
  }
}

// 5 type checking function used for DebtorDeliveryMethod
export function deliveryMethodIsMail(
  method: any,
): method is { mail: MailDelivery } {
  return typeof method === "object" && "mail" in method;
}

export function deliveryMethodIsEmail(
  method: any,
): method is { email: EmailDelivery } {
  return typeof method === "object" && "email" in method;
}

export function deliveryMethodIsSms(
  method: any,
): method is { sms: SmsDelivery } {
  return typeof method === "object" && "sms" in method;
}

export function deliveryMethodIsEInvoicePerson(
  method: any,
): method is { eInvoicePerson: EInvoiceBank } {
  return typeof method === "object" && "eInvoicePerson" in method;
}

export function deliveryMethodIsEInvoiceCompany(
  method: any,
): method is { eInvoiceCompany: EInvoiceCompany } {
  return typeof method === "object" && "eInvoiceCompany" in method;
}
